import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import StatusBadge from "@/components/common/StatusBadge";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Check, X, ClockCounterClockwise, User, Flask, Truck, CheckCircle, XCircle } from "@phosphor-icons/react";

const fmt = (n) => (n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });

export default function ApprovalCenterPage() {
  const [tab, setTab] = useState("do");
  const [doRows, setDoRows] = useState([]);
  const [spbRows, setSpbRows] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);        // {kind, row}
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmFor, setConfirmFor] = useState(null);      // {kind:"do"|"spb", row}
  const [successFor, setSuccessFor] = useState(null);      // {kind, title, message, ref}

  const load = async () => {
    try {
      const [d, s] = await Promise.all([
        api.get("/delivery/pending-approval"),
        api.get("/receiving/pending-spb"),
      ]);
      setDoRows(d.data || []);
      setSpbRows(s.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  useAutoRefresh(load);

  const doApprove = async () => {
    if (!confirmFor) return;
    setSubmitting(true);
    try {
      if (confirmFor.kind === "do") {
        await api.post(`/delivery/${confirmFor.row.id}/approve`);
        setSuccessFor({
          kind: "do",
          title: "DO Berhasil Disetujui",
          message: `Delivery Order untuk ${confirmFor.row.customer} telah disetujui. Loading process dapat dimulai.`,
          ref: confirmFor.row.do_no,
        });
      } else {
        const { data } = await api.post(`/receiving/${confirmFor.row.id}/spb-approve`, {});
        setSuccessFor({
          kind: "spb",
          title: "SPB Berhasil Diterbitkan",
          message: `Surat Perintah Bongkar untuk ${confirmFor.row.supplier} telah terbit. Admin Pabrik dapat mencetak SPB dan memulai proses bongkar.`,
          ref: data.spb_no || confirmFor.row.ref_no,
        });
      }
      setConfirmFor(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setConfirmFor(null);
    } finally {
      setSubmitting(false);
    }
  };

  const doReject = async () => {
    if (!reason.trim()) return toast.error("Alasan penolakan wajib diisi");
    setSubmitting(true);
    try {
      if (rejectFor.kind === "do") {
        await api.post(`/delivery/${rejectFor.row.id}/reject`, { reason });
        setSuccessFor({
          kind: "do_reject",
          title: "DO Ditolak",
          message: `Delivery Order untuk ${rejectFor.row.customer} telah ditolak. Alasan sudah tercatat.`,
          ref: rejectFor.row.do_no,
        });
      } else {
        await api.post(`/receiving/${rejectFor.row.id}/spb-reject`, { reason });
        setSuccessFor({
          kind: "spb_reject",
          title: "SPB Ditolak",
          message: `Penerbitan SPB untuk ${rejectFor.row.supplier} telah ditolak. Alasan sudah tercatat, Petugas Lab dapat menyesuaikan.`,
          ref: rejectFor.row.ref_no,
        });
      }
      setRejectFor(null); setReason("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const totalPending = doRows.length + spbRows.length;

  return (
    <AppShell
      title="Pusat Persetujuan"
      subtitle={`${totalPending} item menunggu persetujuan Pengawas Pabrik`}
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 bg-white border border-slate-200 p-1 rounded-lg shadow-sm h-auto">
          <TabsTrigger value="do" data-testid="approval-tab-do"
            className="gap-2 data-[state=active]:bg-sky-600 data-[state=active]:text-white px-5 py-2 rounded-md text-sm font-semibold">
            <Truck size={16} weight="bold" /> Delivery Order ({doRows.length})
          </TabsTrigger>
          <TabsTrigger value="spb" data-testid="approval-tab-spb"
            className="gap-2 data-[state=active]:bg-amber-600 data-[state=active]:text-white px-5 py-2 rounded-md text-sm font-semibold">
            <Flask size={16} weight="bold" /> SPB Receiving ({spbRows.length})
          </TabsTrigger>
        </TabsList>

        {/* DO tab */}
        <TabsContent value="do" className="mt-0">
          {doRows.length === 0 ? <EmptyState label="Tidak ada DO yang menunggu persetujuan." /> : (
            <div className="space-y-3">
              {doRows.map((r) => (
                <div key={r.id} className="rounded-xl bg-white border border-slate-200 shadow-sm p-4 sm:p-5" data-testid={`approval-card-${r.do_no}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 pb-3 border-b border-slate-100">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-bold text-slate-900 text-base sm:text-lg">{r.do_no}</span>
                        <StatusBadge status="pending_approval" />
                      </div>
                      <div className="text-sm text-slate-600 mt-1 truncate">{r.customer}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Target</div>
                      <div className="font-mono font-bold text-lg sm:text-xl text-sky-700">
                        {fmt(r.target_qty)} <span className="text-xs">kg</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 text-[13px]">
                    <Info label="Produk" value={r.product} />
                    <Info label="Truck" value={r.truck_no} mono />
                    <Info label="Driver" value={r.driver} />
                    <Info label="Jadwal" value={r.schedule_date ? new Date(r.schedule_date).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "-"} />
                  </div>
                  {r.notes && <div className="rounded-md bg-slate-50 border border-slate-100 p-2.5 text-[12px] text-slate-700 mb-3"><b>Catatan:</b> {r.notes}</div>}
                  <MetaLine created_by={r.created_by_name || r.created_by} created_at={r.created_at} />
                  <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
                    <Button variant="outline" onClick={() => setRejectFor({ kind: "do", row: r })} data-testid={`approval-reject-${r.do_no}`}
                      className="border-red-200 text-red-700 hover:bg-red-50 h-11 gap-2"><X size={16} weight="bold" /> Tolak</Button>
                    <Button onClick={() => setConfirmFor({ kind: "do", row: r })} data-testid={`approval-approve-${r.do_no}`}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 gap-2"><Check size={16} weight="bold" /> Setujui DO</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SPB tab */}
        <TabsContent value="spb" className="mt-0">
          {spbRows.length === 0 ? <EmptyState label="Tidak ada receiving yang menunggu SPB." /> : (
            <div className="space-y-3">
              {spbRows.map((r) => {
                const lab = r.lab_result || {};
                return (
                  <div key={r.id} className="rounded-xl bg-white border border-amber-200 shadow-sm p-4 sm:p-5" data-testid={`spb-card-${r.ref_no}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3 pb-3 border-b border-amber-100">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-slate-900 text-base sm:text-lg">{r.ref_no}</span>
                          <StatusBadge status={r.status} />
                          {r.status === "spb_rejected" && <span className="text-[10px] bg-red-100 text-red-800 border border-red-200 px-1.5 py-0.5 rounded-md uppercase font-bold">Sebelumnya ditolak</span>}
                        </div>
                        <div className="text-sm text-slate-700 font-semibold mt-1 truncate">{r.supplier} · {r.material}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Gross In</div>
                        <div className="font-mono font-bold text-lg text-amber-700">{fmt(r.gross_in)} <span className="text-xs">kg</span></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 text-[13px]">
                      <Info label="Truck" value={r.truck_no} mono />
                      <Info label="Driver" value={r.driver} />
                      <Info label="Est. Qty" value={`${fmt(r.estimated_qty)} L`} mono />
                      <Info label="Tank" value={r.destination_tank} />
                    </div>

                    {/* Lab result table */}
                    <div className="rounded-md bg-slate-50 border border-slate-100 p-3 mb-3">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-2">Hasil Lab</div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
                        <Info label="Density" value={lab.density?.toFixed?.(3) || lab.density_used?.toFixed?.(3) || "—"} mono />
                        <Info label="Yield %" value={`${lab.yield_rate ?? "—"}%`} mono />
                        <Info label="Bensin %" value={`${lab.bensin ?? "—"}%`} mono />
                        <Info label="Air %" value={`${lab.air ?? "—"}%`} mono />
                      </div>
                      {lab.notes && <div className="text-[11px] text-slate-600 mt-2 italic">"{lab.notes}"</div>}
                      <div className="text-[10px] text-slate-500 font-mono mt-1.5">Diinput oleh {lab.submitted_by_name || lab.submitted_by} · {lab.submitted_at ? new Date(lab.submitted_at).toLocaleString("id-ID") : "-"}</div>
                    </div>

                    {r.status === "spb_rejected" && r.spb_reject_reason && (
                      <div className="rounded-md bg-red-50 border border-red-200 p-2.5 text-[12px] text-red-800 mb-3"><b>Alasan penolakan sebelumnya:</b> {r.spb_reject_reason}</div>
                    )}

                    <MetaLine created_by={r.created_by} created_at={r.created_at} />
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
                      <Button variant="outline" onClick={() => setRejectFor({ kind: "spb", row: r })} data-testid={`spb-reject-${r.ref_no}`}
                        className="border-red-200 text-red-700 hover:bg-red-50 h-11 gap-2"><X size={16} weight="bold" /> Tolak</Button>
                      <Button onClick={() => setConfirmFor({ kind: "spb", row: r })} data-testid={`spb-approve-${r.ref_no}`}
                        className="bg-amber-600 hover:bg-amber-700 text-white h-11 gap-2"><Check size={16} weight="bold" /> Terbitkan SPB</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Reject dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              Tolak {rejectFor?.kind === "do" ? `DO ${rejectFor?.row?.do_no}` : `SPB ${rejectFor?.row?.ref_no}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-slate-600">Alasan penolakan akan tercatat di audit trail.</p>
            <Textarea data-testid="approval-reject-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Contoh: Yield % di bawah threshold minimum, dst." autoFocus />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => { setRejectFor(null); setReason(""); }}>Batal</Button>
              <Button onClick={doReject} disabled={submitting} data-testid="approval-reject-confirm"
                className="bg-red-600 hover:bg-red-700 text-white">
                {submitting ? "Memproses…" : "Tolak"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Konfirmasi Dialog — sebelum Approve DO/SPB */}
      <Dialog open={!!confirmFor} onOpenChange={(o) => !o && !submitting && setConfirmFor(null)}>
        <DialogContent className="max-w-md" data-testid="approval-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              {confirmFor?.kind === "spb"
                ? <><Flask size={22} weight="fill" className="text-amber-600" /> Terbitkan SPB?</>
                : <><Truck size={22} weight="fill" className="text-emerald-600" /> Setujui DO?</>}
            </DialogTitle>
            <DialogDescription className="text-[13px] pt-2">
              {confirmFor?.kind === "spb"
                ? "Setelah SPB diterbitkan, Admin Pabrik dapat mencetak SPB dan memulai proses bongkar. Aksi ini tercatat di riwayat."
                : "Setelah DO disetujui, Operator Loading dapat memulai proses pengisian. Aksi ini tercatat di riwayat."}
            </DialogDescription>
          </DialogHeader>
          {confirmFor && (
            <div className={`rounded-lg border-2 p-4 my-2 ${confirmFor.kind === "spb" ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {confirmFor.kind === "spb" ? "No. Referensi" : "No. DO"}
                  </div>
                  <div className="font-mono font-bold text-base text-slate-900 truncate">
                    {confirmFor.kind === "spb" ? confirmFor.row.ref_no : confirmFor.row.do_no}
                  </div>
                  <div className="text-[13px] text-slate-700 mt-1 truncate">
                    {confirmFor.kind === "spb" ? confirmFor.row.supplier : confirmFor.row.customer}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    {confirmFor.kind === "spb" ? "Gross In" : "Target"}
                  </div>
                  <div className={`font-mono font-bold text-lg ${confirmFor.kind === "spb" ? "text-amber-700" : "text-emerald-700"}`}>
                    {fmt(confirmFor.kind === "spb" ? confirmFor.row.gross_in : confirmFor.row.target_qty)}
                    <span className="text-xs"> {confirmFor.kind === "spb" ? "kg" : "kg"}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 mt-1 grid grid-cols-2 sm:flex sm:justify-end">
            <Button
              variant="outline"
              disabled={submitting}
              onClick={() => setConfirmFor(null)}
              data-testid="approval-confirm-cancel"
              className="h-12 text-base"
            >
              Batal
            </Button>
            <Button
              onClick={doApprove}
              disabled={submitting}
              data-testid="approval-confirm-yes"
              className={`h-12 text-base gap-2 text-white ${confirmFor?.kind === "spb" ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
            >
              <Check size={18} weight="bold" />
              {submitting ? "Memproses…" : (confirmFor?.kind === "spb" ? "Ya, Terbitkan SPB" : "Ya, Setujui")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sukses Dialog — feedback besar setelah action berhasil */}
      <Dialog open={!!successFor} onOpenChange={(o) => !o && setSuccessFor(null)}>
        <DialogContent className="max-w-md" data-testid="approval-success-dialog">
          <div className="text-center py-2">
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${successFor?.kind?.includes("reject") ? "bg-red-100" : "bg-emerald-100"}`}>
              {successFor?.kind?.includes("reject")
                ? <XCircle size={48} weight="fill" className="text-red-600" />
                : <CheckCircle size={48} weight="fill" className="text-emerald-600" />}
            </div>
            <DialogTitle className="font-heading text-2xl mb-2">
              {successFor?.title}
            </DialogTitle>
            {successFor?.ref && (
              <div className={`inline-block font-mono font-bold text-base px-3 py-1.5 rounded-md border-2 mb-3 ${successFor?.kind?.includes("reject") ? "bg-red-50 border-red-200 text-red-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}
                   data-testid="approval-success-ref">
                {successFor.ref}
              </div>
            )}
            <DialogDescription className="text-[14px] text-slate-700 leading-relaxed px-2">
              {successFor?.message}
            </DialogDescription>
          </div>
          <DialogFooter className="mt-2">
            <Button
              onClick={() => setSuccessFor(null)}
              data-testid="approval-success-ok"
              className="w-full h-12 text-base font-bold bg-slate-900 hover:bg-slate-800 text-white"
            >
              OK, Mengerti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </AppShell>
  );
}

function EmptyState({ label }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-10 text-center shadow-sm">
      <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-3">
        <Check size={22} weight="bold" className="text-emerald-600" />
      </div>
      <h3 className="font-heading text-lg font-bold text-slate-900">Semua sudah tertangani</h3>
      <p className="text-sm text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function MetaLine({ created_by, created_at }) {
  return (
    <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-3 flex-wrap">
      <User size={12} weight="duotone" /> Diajukan oleh <b className="text-slate-700">{created_by || "—"}</b>
      <span className="mx-1">·</span>
      <ClockCounterClockwise size={12} weight="duotone" />
      {created_at ? new Date(created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "—"}
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">{label}</div>
      <div className={`text-slate-800 mt-0.5 ${mono ? "font-mono" : ""} truncate`}>{value || "—"}</div>
    </div>
  );
}
